// De qué cadena(s) puede ver ventas el usuario, según sus asignaciones de rol.
//
// Existe porque el dropdown de cadenas NO es universal: `GET /retailers` exige scope TENANT, y el
// Admin VL real es LEVEL_1 sobre SU cadena. Para él ese endpoint responde 403 y la pantalla quedaba
// atascada en "Elige una cadena" sin poder elegir nada.
//
// La cadena de un LEVEL_1 ya viaja en la sesión: es el `scopeRef` de su asignación. Derivarla de ahí
// evita la llamada prohibida y le ahorra un paso que además no podía completar.
import type { RoleAssignmentInfoLocal } from '@/api/types/MeLocal';

export interface RetailerScope {
    /**
     * La cadena a la que el usuario está acotado, o `null` si puede elegir entre varias.
     * Cuando viene con valor, la pantalla la fija y no ofrece selector.
     */
    fixedRetailerId: string | null;
    /** `true` cuando el usuario abarca todo el silo (TENANT/PLATFORM) y sí puede elegir. */
    canChoose: boolean;
}

// Cadena LEVEL_1 del usuario, ignorando TENANT/PLATFORM de otros roles; null si tiene cero o varias.
export function resolveOwnRetailerId(
    assignments: readonly RoleAssignmentInfoLocal[],
): string | null {
    const retailerIds = [
        ...new Set(assignments.filter((a) => a.scope === 'LEVEL_1').map((a) => a.scopeRef)),
    ];
    return retailerIds.length === 1 ? retailerIds[0] : null;
}

export function resolveRetailerScope(assignments: RoleAssignmentInfoLocal[]): RetailerScope {
    // TENANT y PLATFORM ven el silo entero: para ellos el selector es legítimo.
    const coversSilo = assignments.some((a) => a.scope === 'TENANT' || a.scope === 'PLATFORM');
    if (coversSilo) return { fixedRetailerId: null, canChoose: true };

    return { fixedRetailerId: resolveOwnRetailerId(assignments), canChoose: false };
}
